/* SkillSlider — Open Play Competitive Mode self-assessment (iter515).

   Player drags 1→10 with live level descriptions. Only rendered when the
   session runs in Competitive Mode. The rating is used only to SEED the
   first match — after that, matchmaking is driven by actual results (see
   backend Elo update in open_play_algorithm.py).

   Design notes:
   - Framed as "where should we start you?" — lowers stakes, invites honesty.
   - Levels 1-3 cool cyan → 4-6 emerald → 7-8 amber → 9-10 red. Track color
     shifts as you drag so the visual matches the label.
   - No hidden numbers surfaced to player — the internal `true_rating` never
     appears on the player's screen. */
import { useMemo } from 'react';
import { Swords, UserCheck } from 'lucide-react';
import { fmtDupr } from '../utils/skillLevels';

const LEVELS = [
  { n: 1,  label: 'Absolute Beginner', desc: "First time holding a paddle/racket. Learning basic grip and swing." },
  { n: 2,  label: 'Newbie',            desc: "Can rally a few shots. Low stamina. Still learning rules & scoring." },
  { n: 3,  label: 'Casual Recreational', desc: "Can serve and return. Struggles with consistency and placement." },
  { n: 4,  label: 'Improving Recreational', desc: "Consistent on medium-paced shots. Basic strategy. Comfortable in social games." },
  { n: 5,  label: 'Solid Club Player', desc: "Reliable strokes, good stamina, understands positioning. Just starting competitive play." },
  { n: 6,  label: 'Competitive Club',  desc: "Executes shot variety, uses spin & placement intentionally. Regular in local tournaments." },
  { n: 7,  label: 'Advanced Competitive', desc: "Strong footwork, tactical awareness, consistent under pressure. Wins local tournaments." },
  { n: 8,  label: 'Regional Tournament', desc: "Compete at provincial/state level. Refined technique across all shots." },
  { n: 9,  label: 'National Level',    desc: "High-performance training. Compete nationally." },
  { n: 10, label: 'Elite / Semi-Pro',  desc: "Sponsored, coached, or on a national team pathway." },
];

function trackColor(v) {
  if (v <= 3) return '#06B6D4';   // cyan — cool
  if (v <= 6) return '#22C55E';   // emerald — mid
  if (v <= 8) return '#F59E0B';   // amber — warm
  return '#EF4444';                // red — elite
}

export default function SkillSlider({ value, onChange, disabled = false, welcomeBack = null, sliderId = 'skill-slider-input' }) {
  // `sliderId` must be unique when several sliders render on one page (team
  // registration) — the thumb-color <style> below targets it by data-testid.
  const level = useMemo(
    () => LEVELS.find(l => l.n === Math.round(value)) || LEVELS[4],
    [value]
  );
  const accent = trackColor(value);
  const pct = ((value - 1) / 9) * 100;

  return (
    <div data-testid="skill-slider-wrap"
      style={{
        background: '#0F0F0F', border: `1px solid ${accent}44`, borderRadius: 10,
        padding: '16px 18px', marginTop: 4,
      }}>
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-[10px] font-black uppercase inline-flex items-center gap-1.5" style={{ color: accent, letterSpacing: 2 }}>
          <Swords size={12} style={{ flexShrink: 0 }} /> Rate your skill
        </p>
        <p className="text-2xl font-black text-right" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>
          {Math.round(value)}<span style={{ color: '#555', fontSize: 13, fontWeight: 700 }}>/10</span>
          <span data-testid={`${sliderId}-dupr`} className="block text-[10px] font-bold" style={{ color: '#737373', fontFamily: 'inherit', letterSpacing: 0.5 }}>
            ≈ DUPR {fmtDupr(value)}
          </span>
        </p>
      </div>

      {welcomeBack && (
        <p data-testid="skill-slider-welcome" className="text-[11px] mb-2 inline-flex items-start gap-1.5"
          style={{ color: '#22C55E' }}>
          <UserCheck size={13} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>Welcome back, {welcomeBack.name}! We've got your level from your last {welcomeBack.games_played} game{welcomeBack.games_played === 1 ? '' : 's'}.</span>
        </p>
      )}

      <div style={{ position: 'relative', padding: '10px 0 4px' }}>
        {/* Track background */}
        <div style={{
          position: 'absolute', top: '50%', left: 0, right: 0, height: 6,
          background: '#1F1F1F', borderRadius: 3, transform: 'translateY(-50%)',
        }} />
        {/* Track fill */}
        <div style={{
          position: 'absolute', top: '50%', left: 0, width: `${pct}%`, height: 6,
          background: `linear-gradient(90deg, #06B6D4 0%, #22C55E 40%, #F59E0B 75%, #EF4444 100%)`,
          borderRadius: 3, transform: 'translateY(-50%)',
        }} />
        <input
          data-testid={sliderId}
          type="range" min={1} max={10} step={1}
          value={value}
          disabled={disabled}
          onChange={e => onChange(Number(e.target.value))}
          style={{
            position: 'relative', width: '100%', appearance: 'none',
            background: 'transparent', height: 44, margin: 0, padding: 0,
            outline: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        />
        <style>{`
          input[data-testid="${sliderId}"]::-webkit-slider-thumb {
            appearance: none;
            width: 26px; height: 26px; border-radius: 50%;
            background: #fff; border: 3px solid ${accent};
            cursor: pointer; box-shadow: 0 2px 6px rgba(0,0,0,0.5);
          }
          input[data-testid="${sliderId}"]::-moz-range-thumb {
            width: 26px; height: 26px; border-radius: 50%;
            background: #fff; border: 3px solid ${accent};
            cursor: pointer;
          }
        `}</style>
      </div>

      <div style={{
        marginTop: 10, background: '#0A0A0A', border: `1px solid ${accent}33`,
        borderRadius: 8, padding: '10px 12px',
      }}>
        <p className="text-xs font-bold mb-1" style={{ color: accent }}>
          Level {level.n} — {level.label}
        </p>
        <p className="text-[11px]" style={{ color: '#A3A3A3', lineHeight: 1.5 }}>
          {level.desc}
        </p>
      </div>

      <p className="text-[10px] mt-2" style={{ color: '#555' }}>
        Slide to your comfort level — we'll fine-tune from your first game.
        This just picks your first pairing; after that, matchmaking uses your actual results.
      </p>
    </div>
  );
}
